import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getApiLanguage } from "src/services/commonLanguageHeader";
import {
  createUser,
  deleteUser,
  getClientLanguage,
  getDistributorList,
  getUserActivities,
  getUserById,
  getUserDevices,
  getUserRoleLookup,
  getUserSystems,
  getUserTimedData,
  getUsersList,
  resendAccessEmail,
  saveAllowedSystem,
  saveUserActivities,
  saveUserTimedData,
  saveUserVehicles,
  updateUser,
  updateUserActiveStatus,
  updateUserStatus,
  type UserActivityPermissionPayload,
  type UserFormPayload,
  type ResendAccessEmailPayload,
  type UserTimedDataPayload,
  type UserListParams,
  type UserStatusParams,
} from "src/services/userService";
import { useGeneralRefetchOverride } from "./general.queries";
import { QueryNames } from "./queries.constant";

export const useQueryGetUsersList = (
  params: UserListParams = {},
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.userList, params, language],
      queryFn: () => getUsersList(params, language),
      refetchOnMount: true,
      retry: false,
      ...options,
    }),
    options.enabled !== false
  );
};

export const useQueryGetUserById = (
  id?: string | number,
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  const enabled = Boolean(id) && options.enabled !== false;

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.userDetail, id, language],
      queryFn: () => getUserById(id!, language),
      enabled,
      refetchOnMount: true,
      retry: false,
      ...options,
    }),
    enabled
  );
};

export const useQueryGetUserRoleLookup = (
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  const enabled = options.enabled !== false;

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.userRoleLookup, language],
      queryFn: () => getUserRoleLookup(language),
      enabled,
      retry: false,
      ...options,
    }),
    enabled
  );
};

export const useQueryGetUserDistributorLookup = (
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  const enabled = options.enabled !== false;

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.userDistributorLookup, language],
      queryFn: () => getDistributorList(language),
      enabled,
      retry: false,
      ...options,
    }),
    enabled
  );
};

export const useQueryGetUserClientLanguage = (
  clientId?: string | number,
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  const enabled = Boolean(clientId) && options.enabled !== false;

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.userClientLanguage, clientId, language],
      queryFn: () => getClientLanguage(clientId!, language),
      enabled,
      retry: false,
      staleTime: 0,
      ...options,
    }),
    enabled
  );
};

export const useQueryGetUserSystems = (
  userId?: string | number,
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  const enabled = Boolean(userId) && options.enabled !== false;

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.userSystems, userId, language],
      queryFn: () => getUserSystems(userId!, language),
      enabled,
      refetchOnMount: "always",
      retry: false,
      staleTime: 0,
      ...options,
    }),
    enabled
  );
};

export const useQueryGetUserActivities = (
  userId?: string | number,
  systemId?: string | number,
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  const enabled = Boolean(userId && systemId) && options.enabled !== false;

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.userActivities, userId, systemId, language],
      queryFn: () => getUserActivities(userId!, systemId!, language),
      enabled,
      refetchOnMount: "always",
      retry: false,
      staleTime: 0,
      ...options,
    }),
    enabled
  );
};

export const useQueriesGetUserActivitiesBySystems = (
  userId?: string | number,
  systemIds: Array<string | number> = [],
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  const enabled = Boolean(userId && systemIds.length) && options.enabled !== false;

  return useQueries({
    queries: systemIds.map((systemId) => ({
      queryKey: [...QueryNames.userActivities, userId, systemId, language],
      queryFn: () => getUserActivities(userId!, systemId, language),
      enabled,
      refetchOnMount: "always",
      retry: false,
      staleTime: 0,
      ...options,
    })),
  });
};

export const useQueryGetUserDevices = (
  userId?: string | number,
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  const enabled = Boolean(userId) && options.enabled !== false;

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.userDevices, userId, language],
      queryFn: () => getUserDevices(userId!, language),
      enabled,
      retry: false,
      ...options,
    }),
    enabled
  );
};

export const useQueryGetUserTimedData = (
  userId?: string | number,
  options: { enabled?: boolean } & Record<string, unknown> = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  const enabled = Boolean(userId) && options.enabled !== false;

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.userTimedData, userId, language],
      queryFn: () => getUserTimedData(userId!, language),
      enabled,
      retry: false,
      ...options,
    }),
    enabled
  );
};

export const useMutationCreateUser = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (payload: UserFormPayload) => createUser(payload, language),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QueryNames.userList });
      void queryClient.invalidateQueries({ queryKey: QueryNames.customerLimits });
    },
  });
};

export const useMutationUpdateUser = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: ({ id, payload }: { id: string | number; payload: UserFormPayload }) =>
      updateUser(id, payload, language),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: QueryNames.userList });
      void queryClient.invalidateQueries({ queryKey: QueryNames.customerLimits });
      void queryClient.invalidateQueries({
        queryKey: [...QueryNames.userDetail, variables.id],
      });
    },
  });
};

export const useMutationUpdateUserStatus = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: ({
      id,
      active,
      isCustomerUser,
      params,
    }: {
      id: string | number;
      active: boolean;
      isCustomerUser: boolean;
      params: UserStatusParams;
    }) =>
      isCustomerUser
        ? updateUserStatus(id, params, language)
        : updateUserActiveStatus(id, { id, active }, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.userList });
    },
  });
};

export const useMutationDeleteUser = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (id: string | number) => deleteUser(id, language),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QueryNames.userList });
      void queryClient.invalidateQueries({ queryKey: QueryNames.customerLimits });
    },
  });
};

export const useMutationResendAccessEmail = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (payload: ResendAccessEmailPayload) => resendAccessEmail(payload, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.userList });
    },
  });
};

export const useMutationSaveAllowedSystem = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: ({
      userId,
      systemTypeId,
      enable,
    }: {
      userId: string | number;
      systemTypeId: string | number;
      enable: boolean;
    }) => saveAllowedSystem(userId, systemTypeId, enable, language),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...QueryNames.userSystems, variables.userId],
      });
    },
  });
};

export const useMutationSaveUserActivities = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (payload: UserActivityPermissionPayload[]) => saveUserActivities(payload, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.userActivities });
    },
  });
};

export const useMutationSaveUserVehicles = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: ({
      userId,
      vehicleIds,
    }: {
      userId: string | number;
      vehicleIds: Array<string | number>;
    }) => saveUserVehicles(userId, vehicleIds, language),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...QueryNames.userDevices, variables.userId],
      });
    },
  });
};

export const useMutationSaveUserTimedData = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (payload: UserTimedDataPayload) => saveUserTimedData(payload, language),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...QueryNames.userTimedData, variables.userId],
      });
    },
  });
};
